// Provably fair dealing, protocol UHF-1 (version 1). The name and the "UHF1:" domain tags are fixed
// protocol identifiers from the app's earlier name and stay unchanged so old hands still verify.
//
// This module is the reference implementation of docs/REILUUS.md. It is shared by the server
// (which deals with it) and the browser (which verifies with it), and it only uses SHA-256 and
// HMAC-SHA-256 from @noble/hashes, so identical inputs give identical bytes everywhere.
//
// Summary of the protocol (details and rationale in the document):
//   * When a tournament is published, the server commits to a hash chain of secret seeds:
//     S_{i-1} = H(tag, tournamentId, S_i), commitment C = S_0. Seed S_j is used for draw j and
//     published right after that draw is over; it never reveals S_{j+1}.
//   * When joining, every player's browser picks a random 32-byte seed P_u and sends only its
//     commitment c_u = H(tag, tournamentId, userId, P_u). The seeds are revealed after the
//     tournament starts (participants and commitments are then locked).
//   * Draw j (1 = seating, n + 1 = hand n) gets its entropy from
//     HMAC(S_j, tag ‖ tournamentId ‖ purpose ‖ j ‖ n ‖ participants and their seeds),
//     which drives an HMAC counter-mode byte stream, rejection sampling and a Fisher–Yates shuffle.
import { sha256 } from '@noble/hashes/sha2.js';
import { hmac } from '@noble/hashes/hmac.js';
import type { Card } from './cards.ts';

export const PROTOCOL = 'UHF-1';
/** Length of the server's seed chain: at most this many draws (seating + hands) per tournament. */
export const CHAIN_LENGTH = 10_000;
export const SEED_BYTES = 32;

const TAG = {
  chain: 'UHF1:chain',
  player: 'UHF1:player',
  draw: 'UHF1:draw',
  stream: 'UHF1:stream',
  deck: 'UHF1:deck',
} as const;

// ---------------------------------------------------------------------------
// Encoding

const utf8 = new TextEncoder();

export function toHex(bytes: Uint8Array): string {
  let out = '';
  for (const b of bytes) out += b.toString(16).padStart(2, '0');
  return out;
}

/** Strict hex decoding: lowercase or uppercase pairs only, optional exact length in bytes. */
export function fromHex(hex: string, expectedBytes?: number): Uint8Array {
  if (typeof hex !== 'string' || hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) throw new Error('invalid hex');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  if (expectedBytes !== undefined && out.length !== expectedBytes) throw new Error(`expected ${expectedBytes} bytes`);
  return out;
}

export function isSeedHex(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

function u32(n: number): Uint8Array {
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) throw new Error('uint32 out of range');
  return new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

function concat(parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/**
 * Canonical encoding: every field is prefixed with its length as a 4-byte big-endian integer,
 * so no two different field lists can encode to the same bytes.
 */
export function encodeFields(fields: (Uint8Array | string)[]): Uint8Array {
  const parts: Uint8Array[] = [];
  for (const f of fields) {
    const bytes = typeof f === 'string' ? utf8.encode(f) : f;
    parts.push(u32(bytes.length), bytes);
  }
  return concat(parts);
}

// ---------------------------------------------------------------------------
// Commitments

/** One step of the server chain: S_{i-1} = H(E("UHF1:chain") ‖ E(tournamentId) ‖ E(S_i)). */
export function chainStep(tournamentId: string, seed: Uint8Array): Uint8Array {
  return sha256(encodeFields([TAG.chain, tournamentId, seed]));
}

/** Applies the chain step `times` times: from S_j, `times = j` gives the commitment S_0. */
export function chainBack(tournamentId: string, seed: Uint8Array, times: number): Uint8Array {
  let s = seed;
  for (let i = 0; i < times; i++) s = chainStep(tournamentId, s);
  return s;
}

export function playerCommitment(tournamentId: string, userId: string, seed: Uint8Array): Uint8Array {
  return sha256(encodeFields([TAG.player, tournamentId, userId, seed]));
}

// ---------------------------------------------------------------------------
// Draw entropy and the byte stream

export interface Participant {
  userId: string;
  /** The revealed 32-byte seed, or null when it was not revealed in time (then it is left out). */
  seed: Uint8Array | null;
}

/** Canonical participant order: by user id, comparing the UTF-8 bytes. */
export function canonicalParticipants<T extends { userId: string }>(list: readonly T[]): T[] {
  const key = (s: string) => utf8.encode(s);
  return [...list].sort((a, b) => {
    const x = key(a.userId);
    const y = key(b.userId);
    for (let i = 0; i < Math.min(x.length, y.length); i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
    return x.length - y.length;
  });
}

export type Purpose = 'seating' | 'hand';

/** Chain index of a draw: seating uses 1, hand n uses n + 1. */
export function chainIndexFor(purpose: Purpose, handNo: number): number {
  return purpose === 'seating' ? 1 : handNo + 1;
}

/** The exact bytes that are HMAC'd with the server seed (exported for test vectors). */
export function drawMessage(tournamentId: string, purpose: Purpose, handNo: number, participants: readonly Participant[]): Uint8Array {
  const ordered = canonicalParticipants(participants);
  const ids = new Set(ordered.map((p) => p.userId));
  if (ids.size !== ordered.length) throw new Error('duplicate participant');
  const fields: (Uint8Array | string)[] = [TAG.draw, tournamentId, purpose, u32(chainIndexFor(purpose, handNo)), u32(handNo), u32(ordered.length)];
  for (const p of ordered) {
    if (p.seed !== null && p.seed.length !== SEED_BYTES) throw new Error('player seed must be 32 bytes');
    fields.push(p.userId, p.seed ?? new Uint8Array(0));
  }
  return encodeFields(fields);
}

export function drawEntropy(serverSeed: Uint8Array, tournamentId: string, purpose: Purpose, handNo: number, participants: readonly Participant[]): Uint8Array {
  if (serverSeed.length !== SEED_BYTES) throw new Error('server seed must be 32 bytes');
  return hmac(sha256, serverSeed, drawMessage(tournamentId, purpose, handNo, participants));
}

/**
 * Deterministic random numbers from the draw entropy R: block b = HMAC(R, E("UHF1:stream") ‖ b),
 * read as consecutive 32-bit big-endian integers.
 */
export class DrawStream {
  private block = new Uint8Array(0);
  private offset = 0;
  private counter = 0;
  private readonly entropy: Uint8Array;
  constructor(entropy: Uint8Array) {
    this.entropy = entropy;
  }

  nextUint32(): number {
    if (this.offset + 4 > this.block.length) {
      this.block = hmac(sha256, this.entropy, concat([encodeFields([TAG.stream]), u32(this.counter++)]));
      this.offset = 0;
    }
    const b = this.block;
    const o = this.offset;
    this.offset += 4;
    return ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
  }

  /** Uniform integer in [0, n) without modulo bias (rejection sampling). */
  uniform(n: number): number {
    if (!Number.isInteger(n) || n < 1 || n > 0xffffffff) throw new Error('range out of bounds');
    const limit = 2 ** 32 - (2 ** 32 % n);
    for (;;) {
      const x = this.nextUint32();
      if (x < limit) return x % n;
    }
  }
}

/** Fisher–Yates from the last position down: swap item i with a uniform j in [0, i]. */
export function shuffleWith<T>(items: readonly T[], stream: DrawStream): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = stream.uniform(i + 1);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Initial deck order before shuffling: ranks 2…A, each in suits c, d, h, s. */
export function canonicalDeck(): Card[] {
  const deck: Card[] = [];
  for (const r of '23456789TJQKA') for (const s of 'cdhs') deck.push(`${r}${s}` as Card);
  return deck;
}

export function deckFromEntropy(entropy: Uint8Array): Card[] {
  return shuffleWith(canonicalDeck(), new DrawStream(entropy));
}

/** Short fingerprint of a deck for reports and test vectors. */
export function deckDigest(deck: readonly Card[]): string {
  return toHex(sha256(encodeFields([TAG.deck, deck.join(',')])));
}

/**
 * Seating draw: participants in canonical order are shuffled; seat k + 1 goes to position k.
 * The first button is then drawn from the same stream among the seats 1…count.
 */
export function seatingFromEntropy(entropy: Uint8Array, participants: readonly { userId: string }[]): { seats: { userId: string; seat: number }[]; buttonSeat: number } {
  const stream = new DrawStream(entropy);
  const order = shuffleWith(canonicalParticipants(participants), stream);
  const seats = order.map((p, i) => ({ userId: p.userId, seat: i + 1 }));
  return { seats, buttonSeat: stream.uniform(seats.length) + 1 };
}

// ---------------------------------------------------------------------------
// Dealing: which deck position ends up where

export interface DealLayout {
  /** Hole cards per seat, dealt one at a time in two rounds starting left of the button. */
  holes: { seat: number; cards: Card[] }[];
  /** Burn cards before the flop, turn and river. */
  burned: Card[];
  /** All five board cards in order (flop, turn, river); a hand uses as many as it reached. */
  board: Card[];
}

/** Seats clockwise starting from the first seat after the button. */
export function dealingOrder(seats: readonly number[], buttonSeat: number): number[] {
  const sorted = [...seats].sort((a, b) => a - b);
  return [...sorted.filter((s) => s > buttonSeat), ...sorted.filter((s) => s <= buttonSeat)];
}

export function dealLayout(deck: readonly Card[], seats: readonly number[], buttonSeat: number): DealLayout {
  const order = dealingOrder(seats, buttonSeat);
  let i = 0;
  const holes = new Map<number, Card[]>(order.map((s) => [s, []]));
  for (let round = 0; round < 2; round++) for (const s of order) holes.get(s)!.push(deck[i++]!);
  const burned: Card[] = [];
  const board: Card[] = [];
  for (const n of [3, 1, 1]) {
    burned.push(deck[i++]!);
    for (let k = 0; k < n; k++) board.push(deck[i++]!);
  }
  return { holes: order.map((seat) => ({ seat, cards: holes.get(seat)! })), burned, board };
}

// ---------------------------------------------------------------------------
// Verification of a published hand

export interface HandEvidence {
  protocol: string;
  tournamentId: string;
  handNo: number;
  chainIndex: number;
  chainLength: number;
  /** S_0, published when the tournament was published. */
  serverCommitment: string;
  /** S_j, published when the hand is over (null while it is still being played). */
  serverSeed: string | null;
  /** The server's statement of the draw entropy (checked, never trusted). */
  entropy: string | null;
  participants: { userId: string; displayName: string; commitment: string | null; seed: string | null }[];
  /** Players dealt into the hand and the button. */
  table: { buttonSeat: number; players: { seat: number; userId: string }[] };
  /**
   * Where the table came from: the seating draw for hand 1, otherwise the previous hand's table
   * and who still had chips after it. Lets the verifier check that the server followed the button
   * rules instead of steering cards by moving the button or dealing someone in or out.
   */
  previous?:
    | { kind: 'seating'; seats: { userId: string; seat: number }[]; buttonSeat: number }
    | { kind: 'hand'; buttonSeat: number; players: { seat: number; userId: string; endStack: number }[] };
  /** Every card the server recorded as dealt (published after the hand). */
  dealt: { holes: { seat: number; cards: Card[] }[]; burned: Card[]; board: Card[] } | null;
  /** The public hand history: board and the hands that were shown. */
  history: { board: Card[]; shown: { seat: number; cards: Card[] }[] };
}

export type CheckStatus = 'ok' | 'fail' | 'warn' | 'pending';

export interface Check {
  key: 'protocol' | 'server' | 'players' | 'table' | 'entropy' | 'deck' | 'dealt' | 'history' | 'observed';
  status: CheckStatus;
  detail: string;
}

export interface VerifyResult {
  ok: boolean;
  checks: Check[];
  entropy: string | null;
  deck: Card[] | null;
  deckDigest: string | null;
  layout: DealLayout | null;
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Recomputes everything from the raw evidence. `observed` are cards this device saw during the
 * hand (own hole cards and the board), recorded locally so a later rewrite would be noticed.
 */
export function verifyHand(
  ev: HandEvidence,
  observed?: { hole?: Card[]; seat?: number; board?: Card[]; ownSeed?: { userId: string; seed: string } } | null,
  /**
   * The participants locked at the start (from the seating evidence). Every hand must use exactly
   * these, with the same commitments and seeds: dropping someone for one hand is caught here.
   */
  locked?: HandEvidence['participants'] | null,
): VerifyResult {
  const checks: Check[] = [];
  const result = (): VerifyResult => ({ ok: checks.every((c) => c.status === 'ok' || c.status === 'warn'), checks, entropy, deck, deckDigest: deck ? deckDigest(deck) : null, layout });
  let entropy: string | null = null;
  let deck: Card[] | null = null;
  let layout: DealLayout | null = null;

  if (ev.protocol !== PROTOCOL) {
    checks.push({ key: 'protocol', status: 'fail', detail: `unknown protocol ${ev.protocol}` });
    return result();
  }
  if (ev.chainIndex !== chainIndexFor('hand', ev.handNo) || ev.chainIndex > ev.chainLength) {
    checks.push({ key: 'protocol', status: 'fail', detail: `hand ${ev.handNo} must use chain position ${chainIndexFor('hand', ev.handNo)}, evidence says ${ev.chainIndex}` });
    return result();
  }
  checks.push({ key: 'protocol', status: 'ok', detail: `${PROTOCOL}, chain position ${ev.chainIndex}` });

  // 1. Server seed against the commitment made before anyone joined.
  if (!ev.serverSeed) {
    checks.push({ key: 'server', status: 'pending', detail: 'server seed is published when the hand is over' });
    return result();
  }
  let serverSeed: Uint8Array;
  try {
    serverSeed = fromHex(ev.serverSeed, SEED_BYTES);
  } catch {
    checks.push({ key: 'server', status: 'fail', detail: 'server seed is not 32 bytes of hex' });
    return result();
  }
  const back = toHex(chainBack(ev.tournamentId, serverSeed, ev.chainIndex));
  if (back !== ev.serverCommitment.toLowerCase()) {
    checks.push({ key: 'server', status: 'fail', detail: `hashing the server seed ${ev.chainIndex} times gives ${back}, not the commitment ${ev.serverCommitment}` });
    return result();
  }
  checks.push({ key: 'server', status: 'ok', detail: 'server seed matches the commitment published before registration' });

  // 2. Player seeds against their commitments.
  const participants: Participant[] = [];
  const problems: string[] = [];
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const p of ev.participants) {
    if (seen.has(p.userId)) problems.push(`${p.displayName}: listed twice`);
    seen.add(p.userId);
    if (p.seed === null) {
      missing.push(p.displayName);
      participants.push({ userId: p.userId, seed: null });
      continue;
    }
    if (!isSeedHex(p.seed) || !p.commitment) {
      problems.push(`${p.displayName}: seed without a valid commitment`);
      continue;
    }
    const seed = fromHex(p.seed, SEED_BYTES);
    if (toHex(playerCommitment(ev.tournamentId, p.userId, seed)) !== p.commitment.toLowerCase()) problems.push(`${p.displayName}: seed does not match the commitment`);
    participants.push({ userId: p.userId, seed });
  }
  const tablePlayersMissing = ev.table.players.filter((tp) => !seen.has(tp.userId));
  if (tablePlayersMissing.length > 0) problems.push('a player at the table is not a locked participant');
  if (locked) {
    const key = (p: { userId: string; commitment: string | null; seed: string | null }) => `${p.userId}:${p.commitment ?? ''}:${p.seed ?? ''}`;
    const a = locked.map(key).sort();
    const b = ev.participants.map(key).sort();
    if (!same(a, b)) problems.push('the participants, commitments or seeds differ from those locked at the start');
  }
  if (problems.length > 0) {
    checks.push({ key: 'players', status: 'fail', detail: problems.join('; ') });
    return result();
  }
  checks.push({
    key: 'players',
    status: missing.length > 0 ? 'warn' : 'ok',
    detail: missing.length > 0 ? `seeds match; not revealed in time and left out: ${missing.join(', ')}` : 'every seed matches its commitment',
  });

  // 2b. The table itself follows the rules (who is dealt in, where the button is).
  const tableProblem = checkTable(ev);
  checks.push(
    tableProblem === undefined
      ? { key: 'table', status: 'warn', detail: 'no information about the previous table' }
      : { key: 'table', status: tableProblem ? 'fail' : 'ok', detail: tableProblem ?? 'players and button follow from the previous hand' },
  );

  // 3. Entropy.
  const r = drawEntropy(serverSeed, ev.tournamentId, 'hand', ev.handNo, participants);
  entropy = toHex(r);
  if (ev.entropy !== null && ev.entropy.toLowerCase() !== entropy) {
    checks.push({ key: 'entropy', status: 'fail', detail: `server stated ${ev.entropy}, recomputed ${entropy}` });
    return result();
  }
  checks.push({ key: 'entropy', status: 'ok', detail: entropy });

  // 4. Deck.
  deck = deckFromEntropy(r);
  checks.push({ key: 'deck', status: new Set(deck).size === 52 ? 'ok' : 'fail', detail: `52 cards, fingerprint ${deckDigest(deck)}` });

  // 5. Dealt cards recorded by the server.
  layout = dealLayout(deck, ev.table.players.map((p) => p.seat), ev.table.buttonSeat);
  if (!ev.dealt) {
    checks.push({ key: 'dealt', status: 'pending', detail: 'dealt cards are published when the hand is over' });
    return result();
  }
  const dealtProblems: string[] = [];
  for (const h of layout.holes) {
    const rec = ev.dealt.holes.find((x) => x.seat === h.seat);
    if (!rec || !same(rec.cards, h.cards)) dealtProblems.push(`seat ${h.seat}: expected ${h.cards.join(' ')}, recorded ${rec?.cards.join(' ') ?? '–'}`);
  }
  if (ev.dealt.holes.length !== layout.holes.length) dealtProblems.push('recorded hole cards for a different set of seats');
  const n = ev.dealt.board.length;
  if (!same(ev.dealt.board, layout.board.slice(0, n))) dealtProblems.push(`board: expected ${layout.board.slice(0, n).join(' ')}, recorded ${ev.dealt.board.join(' ')}`);
  const burns = n === 0 ? 0 : n === 3 ? 1 : n === 4 ? 2 : 3;
  if (![0, 3, 4, 5].includes(n) || !same(ev.dealt.burned, layout.burned.slice(0, burns))) dealtProblems.push(`burn cards: expected ${layout.burned.slice(0, burns).join(' ')}, recorded ${ev.dealt.burned.join(' ')}`);
  checks.push({ key: 'dealt', status: dealtProblems.length ? 'fail' : 'ok', detail: dealtProblems.length ? dealtProblems.join('; ') : 'hole cards, burn cards and board match the deck' });

  // 6. The public hand history (what everyone saw) against the deck.
  const historyProblems: string[] = [];
  if (!same(ev.history.board, layout.board.slice(0, ev.history.board.length)) || ev.history.board.length !== n) historyProblems.push('the board in the hand history differs');
  for (const s of ev.history.shown) {
    const h = layout.holes.find((x) => x.seat === s.seat);
    if (!h || !same([...s.cards].sort(), [...h.cards].sort())) historyProblems.push(`seat ${s.seat} showed ${s.cards.join(' ')}, deck gives ${h?.cards.join(' ') ?? '–'}`);
  }
  checks.push({ key: 'history', status: historyProblems.length ? 'fail' : 'ok', detail: historyProblems.length ? historyProblems.join('; ') : 'board and shown hands in the hand history match' });

  // 7. Optional: what this device saw while playing.
  if (observed && (observed.hole?.length || observed.board?.length || observed.ownSeed)) {
    const obsProblems: string[] = [];
    // The seed this device revealed must be the one used: a server "losing" it would be caught here.
    if (observed.ownSeed) {
      const listed = ev.participants.find((p) => p.userId === observed.ownSeed!.userId);
      if (!listed || listed.seed !== observed.ownSeed.seed) obsProblems.push('the seed this device revealed is missing or different in the evidence');
    }
    if (observed.hole?.length && observed.seat !== undefined) {
      const h = layout.holes.find((x) => x.seat === observed.seat);
      if (!h || !same([...observed.hole].sort(), [...h.cards].sort())) obsProblems.push(`your cards on this device were ${observed.hole.join(' ')}`);
    }
    if (observed.board?.length && !same(observed.board, layout.board.slice(0, observed.board.length))) obsProblems.push(`the board seen on this device was ${observed.board.join(' ')}`);
    checks.push({ key: 'observed', status: obsProblems.length ? 'fail' : 'ok', detail: obsProblems.length ? obsProblems.join('; ') : 'matches what this device saw during the hand' });
  }
  return result();
}

/**
 * The button rule of the game: the button moves to the next seat still in play; heads-up the
 * previous big blind takes the button if still in. Returns an error text, null when consistent,
 * or undefined when there is nothing to compare with.
 */
export function checkTable(ev: Pick<HandEvidence, 'table' | 'previous'>): string | null | undefined {
  const prev = ev.previous;
  if (!prev) return undefined;
  const seats = ev.table.players.map((p) => `${p.seat}:${p.userId}`).sort();
  let expectedPlayers: string[];
  let expectedButton: number;
  if (prev.kind === 'seating') {
    expectedPlayers = prev.seats.map((s) => `${s.seat}:${s.userId}`).sort();
    expectedButton = prev.buttonSeat;
  } else {
    const alive = prev.players.filter((p) => p.endStack > 0);
    expectedPlayers = alive.map((p) => `${p.seat}:${p.userId}`).sort();
    const prevSeats = prev.players.map((p) => p.seat);
    const prevOrder = dealingOrder(prevSeats, prev.buttonSeat);
    const prevBb = prevSeats.length === 2 ? prevOrder[0]! : prevOrder[1]!;
    const aliveSeats = alive.map((p) => p.seat);
    expectedButton = aliveSeats.length === 2 && aliveSeats.includes(prevBb) ? prevBb : dealingOrder(aliveSeats, prev.buttonSeat)[0]!;
  }
  if (!same(seats, expectedPlayers)) return `players dealt in: expected ${expectedPlayers.join(', ')}, recorded ${seats.join(', ')}`;
  if (ev.table.buttonSeat !== expectedButton) return `button: expected seat ${expectedButton}, recorded seat ${ev.table.buttonSeat}`;
  return null;
}

// ---------------------------------------------------------------------------
// Seating verification

export interface SeatingEvidence {
  protocol: string;
  tournamentId: string;
  serverCommitment: string;
  serverSeed: string | null;
  participants: HandEvidence['participants'];
  seats: { userId: string; seat: number }[];
  buttonSeat: number;
}

export function verifySeating(ev: SeatingEvidence): { ok: boolean; detail: string } {
  if (ev.protocol !== PROTOCOL) return { ok: false, detail: 'unknown protocol' };
  if (!isSeedHex(ev.serverSeed)) return { ok: false, detail: 'server seed not published' };
  const s1 = fromHex(ev.serverSeed, SEED_BYTES);
  if (toHex(chainBack(ev.tournamentId, s1, 1)) !== ev.serverCommitment.toLowerCase()) return { ok: false, detail: 'server seed does not match the commitment' };
  const participants: Participant[] = [];
  for (const p of ev.participants) {
    if (p.seed !== null) {
      if (!isSeedHex(p.seed) || !p.commitment || toHex(playerCommitment(ev.tournamentId, p.userId, fromHex(p.seed))) !== p.commitment) {
        return { ok: false, detail: `${p.displayName}: seed does not match the commitment` };
      }
    }
    participants.push({ userId: p.userId, seed: p.seed === null ? null : fromHex(p.seed) });
  }
  const expected = seatingFromEntropy(drawEntropy(s1, ev.tournamentId, 'seating', 0, participants), participants);
  const seatOf = new Map(ev.seats.map((s) => [s.userId, s.seat]));
  const ok = expected.buttonSeat === ev.buttonSeat && expected.seats.length === ev.seats.length && expected.seats.every((s) => seatOf.get(s.userId) === s.seat);
  return { ok, detail: ok ? 'seats and the first button match the draw' : 'seats or the first button differ from the draw' };
}
